"use client";

import { useState } from "react";
import { useMFAVerify } from "@padi/lib/hooks/use-auth";
import { Button } from "@padi/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@padi/ui/input-otp";
import { Input } from "@padi/ui/input";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

interface MFAVerifyFormProps {
  mfaToken: string;
  onBack: () => void;
}

export function MFAVerifyForm({ mfaToken, onBack }: MFAVerifyFormProps) {
  const [code, setCode] = useState("");
  const [isBackup, setIsBackup] = useState(false);
  const { mutate: verify, isPending } = useMFAVerify();

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!code) return;

    // Backup codes are XXXX-XXXX, TOTP is 6 digits
    // The backend handles both in the same 'code' field
    verify({ mfaToken, code });
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="space-y-2 text-center">
        <div className="flex justify-center pb-2">
          <div className="p-3 bg-secondary/10 rounded-full">
            <ShieldCheck className="size-8 text-secondary" />
          </div>
        </div>
        <h2 className="text-xl font-bold">Two-Factor Auth</h2>
        <p className="text-sm text-muted-foreground">
          {isBackup
            ? "Enter one of your 8-character backup codes."
            : "Enter the 6-digit code from your authenticator app."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex justify-center">
          {isBackup ? (
            <Input
              placeholder="XXXX-XXXX"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="text-center font-mono tracking-widest"
              maxLength={9}
              autoFocus
            />
          ) : (
            <InputOTP
              maxLength={6}
              value={code}
              onChange={setCode}
              onComplete={() => verify({ mfaToken, code })}
            >
              <InputOTPGroup>
                {[...Array(6)].map((_, i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          )}
        </div>

        <div className="space-y-3">
          <Button
            type="submit"
            variant="secondary"
            fullWidth
            loading={isPending}
            disabled={isBackup ? code.length < 8 : code.length !== 6}
          >
            Verify
          </Button>

          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={() => {
              setIsBackup(!isBackup);
              setCode("");
            }}
          >
            {isBackup ? "Use Authenticator App" : "Use Backup Code"}
          </Button>

          <button
            type="button"
            onClick={onBack}
            className="flex items-center justify-center gap-2 w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3" />
            Back to Sign In
          </button>
        </div>
      </form>
    </div>
  );
}
